import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SalarySubNav from './SalarySubNav';
import { apiGet, apiSend, paystubUrl } from './api';
import { fmtCurrency, fmtDate, fmtNumber, periodDisplayStatus } from './formatters';
import RecordPaymentModal from './RecordPaymentModal';
import RateHoursEditor from './RateHoursEditor';
import type { SalaryEntry, SalaryPeriod, SalaryPayment } from './types';
import DataTable from '../../shared/components/DataTable';
import './SalaryModule.css';
import { useEditProtection } from '../../shared/components/EditProtection';

const PeriodDetail: React.FC = () => {
  const { sessionToken } = useAuth();
  const { id } = useParams<{ id: string }>();
  const periodId = Number(id);
  const [period, setPeriod] = useState<SalaryPeriod | null>(null);
  const [entries, setEntries] = useState<SalaryEntry[]>([]);
  const [payments, setPayments] = useState<SalaryPayment[]>([]);
  const [hoursDraft, setHoursDraft] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const dirtyIds = useRef(new Set<number>());
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  useEditProtection(dirtyIds.current.size > 0);
  const [err, setErr] = useState('');
  const [activeEntry, setActiveEntry] = useState<SalaryEntry | null>(null);
  const [splitEntry, setSplitEntry] = useState<SalaryEntry | null>(null);

  const reload = useCallback(async () => {
    try {
      const [entriesRes, payRes] = await Promise.all([
        apiGet<{ period: SalaryPeriod; entries: SalaryEntry[] }>(`/periods/${periodId}/entries`, sessionToken),
        apiGet<{ payments: SalaryPayment[] }>(`/payments?periodId=${periodId}`, sessionToken),
      ]);
      setPeriod(entriesRes.period);
      setEntries(entriesRes.entries);
      setPayments(payRes.payments);
      const draft: Record<number, string> = {};
      entriesRes.entries.forEach((row) => { draft[row.employee_id] = String(row.hours); });
      setHoursDraft(previous => ({ ...draft, ...Object.fromEntries(Object.entries(previous).filter(([key]) => dirtyIds.current.has(Number(key)))) }));
    } catch (ex: any) {
      setErr(ex.message);
    }
  }, [periodId, sessionToken]);

  useEffect(() => { reload(); }, [reload]);

  const saveHours = async (employeeId: number) => {
    const value = hoursDraft[employeeId];
    if (!value?.trim() || !Number.isFinite(Number(value)) || Number(value) < 0) { setErr('Enter valid hours of zero or more.'); return; }
    try {
      setSavingId(employeeId);
      setErr('');
      await apiSend(`/periods/${periodId}/entries`, 'POST', {
        employee_id: employeeId,
        hours: Number(hoursDraft[employeeId] || 0),
      }, sessionToken);
      dirtyIds.current.delete(employeeId);
      setSavedIds(prev => new Set(prev).add(employeeId));
      await reload();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const calculate = async () => {
    try {
      await apiSend(`/periods/${periodId}/calculate`, 'POST', {}, sessionToken);
      await reload();
    } catch (e: any) { setErr(e.message); }
  };

  const close = async () => {
    const missing = payments.filter((p) => !p.paystub_number);
    let prompt = 'Close this period? No further edits will be allowed.';
    if (missing.length > 0) {
      const names = Array.from(new Set(missing.map((p) => p.employee_name).filter(Boolean))).join(', ');
      prompt = `Heads up — ${missing.length} payment${missing.length === 1 ? '' : 's'} in this period ${missing.length === 1 ? 'has' : 'have'} no pay stub generated yet (${names}).\n\nYou can still generate stubs after closing, but it's cleaner to do it before.\n\nClose the period anyway?`;
    }
    if (!window.confirm(prompt)) return;
    try {
      await apiSend(`/periods/${periodId}/close`, 'POST', {}, sessionToken);
      await reload();
    } catch (e: any) { setErr(e.message); }
  };

  const [stubBusyId, setStubBusyId] = useState<number | null>(null);
  const generateStub = async (paymentId: number) => {
    try {
      setStubBusyId(paymentId);
      setErr('');
      await apiSend(`/payments/${paymentId}/paystub/regenerate`, 'POST', {}, sessionToken);
      await reload();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setStubBusyId(null);
    }
  };

  if (!period) {
    return (
      <div className="salary-module">
        <h2>Pay Period</h2>
        <SalarySubNav />
        {err ? <div className="error-banner">{err}</div> : <p>Loading…</p>}
        <Link to="/salary/periods">← Back to Periods</Link>
      </div>
    );
  }

  const regularEntries = entries.filter((e) => !e.is_cash_employee);
  const cashEntries = entries.filter((e) => e.is_cash_employee);
  const totals = regularEntries.reduce((acc, e) => ({
    gross: acc.gross + Number(e.gross),
    tax: acc.tax + Number(e.tax),
    cpp: acc.cpp + Number(e.cpp_employee),
    ei: acc.ei + Number(e.ei_employee),
    net: acc.net + Number(e.net),
    cppEr: acc.cppEr + Number(e.cpp_employer),
    eiEr: acc.eiEr + Number(e.ei_employer),
  }), { gross: 0, tax: 0, cpp: 0, ei: 0, net: 0, cppEr: 0, eiEr: 0 });

  const readOnly = period.status === 'closed' || period.status === 'voided';

  const payableEntries = entries.filter((e) => Number(e.net) > 0);
  const displayStatus = periodDisplayStatus({
    status: period.status,
    endDate: period.end_date,
    totalNet: entries.reduce((s, e) => s + Number(e.net), 0),
    totalPaid: entries.reduce((s, e) => s + Number(e.total_paid || 0), 0),
    allPaid:
      payableEntries.length > 0 &&
      payableEntries.every((e) => Number(e.total_paid || 0) >= Number(e.net)),
  });
  const heroGross = entries.reduce((s, e) => s + Number(e.gross), 0);
  const heroNet = entries.reduce((s, e) => s + Number(e.net), 0);
  const heroPaid = entries.reduce((s, e) => s + Number(e.total_paid || 0), 0);

  return (
    <div className="salary-module salary-period-detail">
      <h2 className="cs-sticky">{fmtDate(period.start_date)} → {fmtDate(period.end_date)}</h2>
      <SalarySubNav />

      {err && <div className="error-banner">{err}</div>}

      {entries.some(e => e.rate_calculation_stale) && <p role="status" className="error-banner">Some amounts need a rate review. Use “Split hours” for periods crossing a rate change, then recalculate before recording payment.</p>}

      <div className="sal-hero">
        <div className="toprow">
          <div>
            <div className="l">Net to pay</div>
            <div className="big num">{fmtCurrency(heroNet)}</div>
            {(period as any).payout_date && <div className="rng">Payout {fmtDate((period as any).payout_date)}</div>}
          </div>
          <span className={`sal-chip sc-${displayStatus}`}>{displayStatus}</span>
        </div>
        <div className="split">
          <div><div className="l">Gross</div><div className="v num">{fmtCurrency(heroGross)}</div></div>
          <div><div className="l">Deductions</div><div className="v num">{fmtCurrency(heroGross - heroNet)}</div></div>
          <div><div className="l">Paid</div><div className="v num">{fmtCurrency(heroPaid)}</div></div>
        </div>
        <div className="sal-foot">
          <button className="salary-btn secondary" onClick={calculate} disabled={readOnly || dirtyIds.current.size > 0 || savingId !== null}>↻ Recalculate</button>
          <button className="salary-btn danger" onClick={close} disabled={readOnly || dirtyIds.current.size > 0 || savingId !== null}>Close period</button>
        </div>
      </div>

      <div className="cs-toolbar salary-period-toolbar">
        <input type="search" aria-label="Search employees" placeholder="Search employees" value={query} onChange={e => setQuery(e.target.value)} />
        <select aria-label="Payroll filter" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All employees</option>
          <option value="hours">Needs hours</option>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
        </select>
      </div>
      {dirtyIds.current.size > 0 && <p role="status">Unsaved hours. Save each changed employee before recalculating or recording payment.</p>}
      <div className="salary-card salary-period-entries">
        <DataTable
          rows={entries.filter(e => (e.employee_name || '').toLowerCase().includes(query.toLowerCase()) && (filter === 'all' || (filter === 'hours' && Number(e.hours) === 0) || (filter === 'unpaid' && Number(e.net) > Number(e.total_paid || 0)) || (filter === 'paid' && Number(e.net) > 0 && Number(e.total_paid || 0) >= Number(e.net))))}
          rowKey={(e) => e.id}
          caption="Pay-period entries"
          moreLabel="Deductions"
          empty={query || filter !== 'all' ? 'No employees match these filters.' : 'No eligible employees for this period. Check start dates and employment status in User Profiles.'}
          rowClass={(e) => {
            const cls = [];
            if (Number(e.total_paid || 0) >= Number(e.net) && Number(e.net) > 0) cls.push('paid');
            if (e.is_cash_employee) cls.push('cash-employee');
            return cls.join(' ') || undefined;
          }}
          columns={[
            {
              key: 'employee', header: 'Employee', priority: 'primary',
              render: (e) => (
                <>
                  {e.employee_name}
                  {e.is_cash_employee && <span className="status-badge cash">Cash</span>}
                  {e.is_active === false && <span className="status-badge inactive">Inactive</span>}
                </>
              ),
            },
            {
              key: 'hours', header: 'Hours', priority: 'summary', numeric: true,
              render: (e) => (
                <input
                  className="salary-hours-input"
                  type="number" inputMode="decimal" min="0" step="0.01"
                  aria-label={`Hours for ${e.employee_name}`}
                  value={hoursDraft[e.employee_id] ?? ''}
                  onChange={(ev) => { dirtyIds.current.add(e.employee_id); setHoursDraft(prev => ({ ...prev, [e.employee_id]: ev.target.value })); }}
                  disabled={readOnly || e.is_period_eligible === false || Number(e.total_paid || 0) > 0 || savingId === e.employee_id}
                />
              ),
            },
            { key: 'rate', header: 'Rate', priority: 'summary', numeric: true, render: (e) => <>
              {e.rate_breakdown?.some(row => row.hours > 0) ? e.rate_breakdown.filter(row => row.hours > 0).map(row => <div key={row.start_date} className="salary-rate-detail">
                {fmtNumber(row.hours)}h × {fmtCurrency(row.hourly_rate)}
                <small>{row.start_date} → {row.end_date}</small>
              </div>) : fmtCurrency(e.hourly_rate)}
              {e.rate_calculation_stale && <small className="salary-rate-detail">Rate review needed</small>}
              {e.rate_history_error && <small className="salary-rate-detail">Add the earlier rate in Pay rate history.</small>}
            </> },
            { key: 'net', header: 'Net', priority: 'summary', numeric: true, render: (e) => <strong>{fmtCurrency(e.net)}</strong> },
            { key: 'paid', header: 'Paid', priority: 'summary', numeric: true, render: (e) => fmtCurrency(Number(e.total_paid || 0)) },
            { key: 'gross', header: 'Gross', priority: 'detail', numeric: true, render: (e) => fmtCurrency(e.gross) },
            { key: 'tax', header: 'Tax', priority: 'detail', numeric: true, render: (e) => (e.is_cash_employee ? '—' : fmtCurrency(e.tax)) },
            { key: 'cpp', header: 'CPP', priority: 'detail', numeric: true, render: (e) => (e.is_cash_employee ? '—' : fmtCurrency(e.cpp_employee)) },
            { key: 'ei', header: 'EI', priority: 'detail', numeric: true, render: (e) => (e.is_cash_employee ? '—' : fmtCurrency(e.ei_employee)) },
          ]}
          actions={(e) => {
            const fullyPaid = Number(e.total_paid || 0) >= Number(e.net) && Number(e.net) > 0;
            return (
              <div className="salary-period-row-actions">
                {(e.rate_segments?.length || 0) > 1 && <button className="salary-btn secondary"
                  disabled={readOnly || e.is_period_eligible === false || Number(e.total_paid || 0) > 0 || savingId !== null}
                  onClick={() => setSplitEntry({ ...e, hours: hoursDraft[e.employee_id] ?? e.hours })}>Split hours</button>}
                <button
                  className="salary-btn secondary"
                  disabled={readOnly || e.is_period_eligible === false || Number(e.total_paid || 0) > 0 || savingId !== null || !dirtyIds.current.has(e.employee_id)}
                  onClick={() => saveHours(e.employee_id)}
                >
                  {savingId === e.employee_id ? 'Saving…' : dirtyIds.current.has(e.employee_id) ? 'Save hours' : savedIds.has(e.employee_id) ? 'Saved' : 'Hours saved'}
                </button>
                <button
                  className="salary-btn"
                  disabled={readOnly || e.is_period_eligible === false || e.rate_calculation_stale || dirtyIds.current.size > 0 || savingId !== null || Number(e.net) <= 0 || fullyPaid}
                  onClick={() => setActiveEntry(e)}
                >
                  {fullyPaid ? 'Paid' : 'Record Payment'}
                </button>
              </div>
            );
          }}
          footer={
            <>
              <tr>
                <td><strong>Regular Totals</strong></td>
                <td className="num"><strong>{fmtNumber(regularEntries.reduce((s, e) => s + Number(e.hours), 0))}</strong></td>
                <td />
                <td className="num"><strong>{fmtCurrency(totals.net)}</strong></td>
                <td />
                <td className="num"><strong>{fmtCurrency(totals.gross)}</strong></td>
                <td className="num"><strong>{fmtCurrency(totals.tax)}</strong></td>
                <td className="num"><strong>{fmtCurrency(totals.cpp)}</strong></td>
                <td className="num"><strong>{fmtCurrency(totals.ei)}</strong></td>
                <td />
              </tr>
              {cashEntries.length > 0 && (
                <tr className="cash-employee">
                  <td><strong>Cash Totals</strong></td>
                  <td className="num"><strong>{fmtNumber(cashEntries.reduce((s, e) => s + Number(e.hours), 0))}</strong></td>
                  <td />
                  <td className="num"><strong>{fmtCurrency(cashEntries.reduce((s, e) => s + Number(e.net), 0))}</strong></td>
                  <td />
                  <td className="num"><strong>{fmtCurrency(cashEntries.reduce((s, e) => s + Number(e.gross), 0))}</strong></td>
                  <td className="num">—</td>
                  <td className="num">—</td>
                  <td className="num">—</td>
                  <td />
                </tr>
              )}
            </>
          }
          footerCard={
            <>
              <div className="cs-record__title">Totals</div>
              <dl className="cs-record__fields">
                <div className="cs-record__field"><dt>Regular hours</dt><dd className="num">{fmtNumber(regularEntries.reduce((s, e) => s + Number(e.hours), 0))}</dd></div>
                <div className="cs-record__field"><dt>Regular net</dt><dd className="num">{fmtCurrency(totals.net)}</dd></div>
                {cashEntries.length > 0 && (
                  <>
                    <div className="cs-record__field"><dt>Cash hours</dt><dd className="num">{fmtNumber(cashEntries.reduce((s, e) => s + Number(e.hours), 0))}</dd></div>
                    <div className="cs-record__field"><dt>Cash net</dt><dd className="num">{fmtCurrency(cashEntries.reduce((s, e) => s + Number(e.net), 0))}</dd></div>
                  </>
                )}
              </dl>
            </>
          }
        />
      </div>

      <div className="salary-card">
        <h3>CRA Liability for This Period</h3>
        <div className="salary-period-liability">
          <div><small>Income tax</small><div><strong>{fmtCurrency(totals.tax)}</strong></div></div>
          <div><small>CPP (emp + er)</small><div><strong>{fmtCurrency(totals.cpp + totals.cppEr)}</strong></div></div>
          <div><small>EI (emp + er)</small><div><strong>{fmtCurrency(totals.ei + totals.eiEr)}</strong></div></div>
          <div>
            <small>Total remittance</small>
            <div><strong>{fmtCurrency(totals.tax + totals.cpp + totals.cppEr + totals.ei + totals.eiEr)}</strong></div>
          </div>
        </div>
      </div>

      <div className="salary-card">
        <h3>Payments</h3>
        <DataTable
          rows={payments}
          rowKey={(p) => p.id}
          caption="Payments"
          moreLabel="Details"
          empty="No payments yet."
          columns={[
            { key: 'employee', header: 'Employee', priority: 'primary', render: (p) => p.employee_name },
            { key: 'amount', header: 'Amount', priority: 'summary', numeric: true, render: (p) => fmtCurrency(p.amount) },
            { key: 'method', header: 'Method', priority: 'summary', render: (p) => p.method },
            { key: 'paid', header: 'Paid At', priority: 'summary', render: (p) => fmtDate(p.paid_at) },
            { key: 'stub', header: 'Stub #', priority: 'detail', render: (p) => (p.is_cash_employee ? 'n/a (cash)' : p.paystub_number || '—') },
          ]}
          actions={(p) => (
            p.is_cash_employee ? (
              <span className="cs-muted-note">Cash — no stub</span>
            ) : p.paystub_number ? (
              <div className="salary-period-row-actions">
                <a href={paystubUrl(p.id, sessionToken)} target="_blank" rel="noreferrer" className="salary-btn secondary">Stub</a>
                <button className="salary-btn secondary" onClick={() => generateStub(p.id)} disabled={stubBusyId === p.id}>
                  {stubBusyId === p.id ? '…' : 'Regenerate'}
                </button>
              </div>
            ) : (
              <button className="salary-btn" onClick={() => generateStub(p.id)} disabled={stubBusyId === p.id}>
                {stubBusyId === p.id ? 'Generating…' : 'Generate Stub'}
              </button>
            )
          )}
        />
      </div>

      {activeEntry && (
        <RecordPaymentModal
          entry={activeEntry}
          onClose={() => setActiveEntry(null)}
          onPaymentRecorded={() => { reload(); }}
        />
      )}
      {splitEntry && <RateHoursEditor entry={splitEntry} onClose={() => setSplitEntry(null)} onSaved={() => { dirtyIds.current.delete(splitEntry.employee_id); setSavedIds(previous => new Set(previous).add(splitEntry.employee_id)); reload(); }} />}
    </div>
  );
};

export default PeriodDetail;
